import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  FormHelperText,
  IconButton,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { RootState } from '../../../app/rootReducer';
import { IconLink } from '../../../common/components/IconLink';
import { FlexCol, FlexRow } from '../../../common/components/v2';
import ElementLocators from '../../../common/ElementLocators';
import { useKeyPress } from '../../../common/hooks/UseKeyPress';
import { useOnClickOutside } from '../../../common/hooks/UseOnClickedOutside';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { TaskTypes } from '../../tasks/types/taskTypes';

import styles from './styles.module.scss';

interface CodeBackInput {
  value: string;
  isActive: boolean;
}

const MIBCodeBack = ({
  input,
  onCodeUpdate,
  onRemove,
}: {
  input: CodeBackInput;
  onCodeUpdate: (code: string) => void;
  onRemove: () => void;
}) => {
  const [mibCode, setMibCode] = useState(input.value);

  const enterKeyPressed = useKeyPress('Enter');

  const inputRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    if (inputRef.current && input.isActive) {
      inputRef.current.querySelector('input')?.focus();
    }
  }, [inputRef]);

  /*
     Update when enter key is pressed
   */
  useEffect(() => {
    onCodeUpdate(mibCode);
  }, [enterKeyPressed]);

  /*
     Update when clicked outside
   */
  useOnClickOutside(inputRef, () => {
    onCodeUpdate(mibCode);
  });

  return (
    <div
      style={{ width: '100%' }}
      ref={inputRef}
      data-tid={`mib-code-input-${input.isActive ? 'active' : 'inactive'}`}
    >
      <FlexRow className={styles.AlignCenter}>
        <FlexCol xs={11}>
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            variant="outlined"
            margin="none"
            fullWidth
            label="Code"
            value={mibCode}
            onChange={event => setMibCode(event.target.value)}
            name="mib-code"
            placeholder="Please enter one code per row"
          />
        </FlexCol>
        <FlexCol xs={1}>
          <IconButton disableRipple onClick={onRemove} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </FlexCol>
      </FlexRow>
    </div>
  );
};

export const MIBCodebacks = ({
  value,
  onChange,
  label,
  required,
  error,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  label: string;
  required?: boolean;
  error?: string;
}) => {
  const latestNoteCodes = useSelector(pickLatestNoteMibCodes);

  const [mibCodes, setMibCodes] = useState<CodeBackInput[]>(mapToInputs(value));

  const appliedNoteCodesRef = useRef<string[]>([]);

  const updateStateToParent = useCallback(() => {
    onChange?.(mapToValue(mibCodes));
  }, [mibCodes, onChange]);

  useEffect(() => {
    setMibCodes(mapToInputs(value));
  }, [value]);

  useEffect(() => {
    if (latestNoteCodes.length === 0) return;
    const prevNoteCodes = appliedNoteCodesRef.current;
    const manualCodes = mapToValue(mibCodes).filter(
      code => !prevNoteCodes.includes(code),
    );
    const mergedValues = [
      ...latestNoteCodes,
      ...manualCodes.filter(code => !latestNoteCodes.includes(code)),
    ];
    appliedNoteCodesRef.current = latestNoteCodes;
    setMibCodes(mapToInputs(mergedValues));
    onChange?.(mergedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestNoteCodes]);

  const updateCode = useCallback(
    (mibCode: string, index: number) => {
      if (mibCode === mibCodes[index].value) return;
      mibCodes[index] = { value: mibCode, isActive: false };
      updateStateToParent();
    },
    [mibCodes, updateStateToParent],
  );

  const removeCode = useCallback(
    (index: number) => {
      mibCodes.splice(index, 1);
      setMibCodes([...mibCodes]);
      updateStateToParent();
    },
    [mibCodes, updateStateToParent],
  );

  const addCode = useCallback(
    (event: any) => {
      event.preventDefault();
      mibCodes.push({ value: '', isActive: true });
      setMibCodes([...mibCodes]);
    },
    [mibCodes],
  );

  const addMoreLink = () => {
    return mibCodes.length < 50 ? (
      <div className={styles.MIBCodebacksContainer}>
        <IconLink
          onClick={addCode}
          icon={faPlus}
          copy={mibCodes.length > 0 ? 'Add More' : 'Add Code'}
          locator={ElementLocators.LOCATOR_MIB_ADD_MORE}
        />
      </div>
    ) : (
      <></>
    );
  };

  const mibCodeBackInputs = () => (
    <>
      {mibCodes.map((mibCode: CodeBackInput, index: number) => {
        return (
          <FlexRow key={uuidv4()}>
            <MIBCodeBack
              input={mibCode}
              onCodeUpdate={code => updateCode(code, index)}
              onRemove={() => removeCode(index)}
            />
          </FlexRow>
        );
      })}
      <FlexRow>{addMoreLink()}</FlexRow>
    </>
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color={!!error ? 'error' : 'textPrimary'} variant="caption">
          {label}
        </Typography>
        {required ? (
          <Typography component={'span'} color="error" variant="caption">
            *
          </Typography>
        ) : null}
      </Box>
      {mibCodeBackInputs()}
      {error && (
        <FormHelperText sx={sx} error>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

const sx: SxProps = { marginTop: 0 };

const mapToInputs = (value?: string[]) => {
  if (!value) return [];
  return value.map((code: string) => ({ value: code, isActive: false }));
};

const mapToValue = (inputs: CodeBackInput[]) =>
  inputs
    .filter(input => input.value.trim().length > 0)
    .map(input => input.value);

export const pickLatestNoteMibCodes = (state: RootState): string[] => {
  const notes = state.notes.notes;
  if (!notes || notes.length === 0) return [];

  const lastNote = notes[notes.length - 1];
  if (!lastNote.mibCodes || lastNote.mibCodes.length === 0) return [];

  const tasks = getAllTasks(state.taskList);
  let latestCodebackTime = 0;
  for (const task of tasks) {
    if (task.taskType === TaskTypes.MIB_CODEBACK && task.createdAt) {
      const t = new Date(task.createdAt).getTime();
      if (t > latestCodebackTime) latestCodebackTime = t;
    }
  }

  const lastNoteTime = new Date(lastNote.date).getTime();
  if (latestCodebackTime > 0 && lastNoteTime <= latestCodebackTime) return [];

  return lastNote.mibCodes;
};
